/** @format */

import {
	ATC_BUTTON_TEXTS,
	BUTTON_ACTIONS,
	BUTTON_LABELS,
	BUTTON_TEXTS,
	CHANGE_STORE_BUTTON_TEXTS,
	LABEL_TEXTS,
	STORE_CLOSED_MESSAGE
} from 'libs/pdp/add-to-cart/data-access/src/lib/+state/fulfillment-buttons/fulfillment-buttons.constants';
import {
	HeaderStoreSelection,
	ISkuLocation,
	NearbyStoreResponse
} from '@shared/data-access/interfaces';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectBopisInventory, selectEcommInventory } from '../product-selection.selectors';
import {
	selectNearbyStores,
	selectPickupTimeText,
	selectUserStoreInfo,
	selectUserStoreName
} from '../header-location/header-location.selectors';

import { FulfillmentButtonInterface } from 'libs/pdp/add-to-cart/data-access/src/lib/interfaces/fulfillment-button.interface';
import { ProductSelectionState } from '@pdp/data-access/interfaces';
import { StoreStatus } from '@shared/data-access/enums';

/**  @description Attributes State Selectors */
const selectProductionSelectionState =
	createFeatureSelector<ProductSelectionState>('productSelection');

export const selectSelectedBundle = createSelector(
	selectProductionSelectionState,
	(state) => state.bundles
);

export const selectSelectedBundleSkus = createSelector(
	selectSelectedBundle,
	(state) => state.selectedSkus
);

export const selectSelectedBundleSkusPartNumbers = createSelector(
	selectSelectedBundleSkus,
	(state) => state.map((product) => product.sku.partNumber)
);

export const areAllSelectedSkusAvailableToShip = createSelector(
	selectSelectedBundleSkus,
	selectEcommInventory,
	(selectedProducts, shippingInventory: ISkuLocation[]) =>
		selectedProducts.every((product) => {
			const inventoryForSku = shippingInventory.find(
				(inventory) => inventory.sku === product.sku.partNumber
			);
			return inventoryForSku.atsqty >= product.quantity;
		})
);

//checks whether all bundle products can be picked up from store
export const findLowestBopisInventories = createSelector(
	selectSelectedBundleSkus,
	selectBopisInventory,
	(selectedProducts, shippingInventory: ISkuLocation[]) => {
		let allProductsAvailableForStorePickup = true;
		let allProductsAvailableToBuyInStore = true;
		selectedProducts.forEach((product) => {
			const inventoryForSku = shippingInventory?.find(
				(inventory) => inventory.sku === product.sku.partNumber
			);

			if (!inventoryForSku || Number(inventoryForSku?.atsqty) < Number(product.quantity)) {
				allProductsAvailableForStorePickup = false;
			}

			if (!inventoryForSku || Number(inventoryForSku?.isaqty) < Number(product.quantity)) {
				allProductsAvailableToBuyInStore = false;
			}
		});

		return { allProductsAvailableForStorePickup, allProductsAvailableToBuyInStore };
	}
);

export const selectStoreNameTitleCase = createSelector(
	selectUserStoreName,
	(storeName: string) =>
		storeName.toLowerCase().replace(/(?:^|\s)\w/g, (match) => match.toUpperCase())
);

//checks for nearby stores that have all selected skus
export const selectBundleNearbyAvailableStoreCount = createSelector(
	selectNearbyStores,
	selectSelectedBundleSkus,
	(nearbyStores, selectedBundleProducts) => {
		let storesThatHaveAllSkus: NearbyStoreResponse[];
		if (nearbyStores) {
			selectedBundleProducts.forEach((product) => {
				//if this is the first product, set array to array from this product
				if (!storesThatHaveAllSkus) {
					storesThatHaveAllSkus = nearbyStores[product.sku.partNumber];
					//if we don't have nearby stores for the sku, return 0 since there are no stores with all products
				} else if (!nearbyStores[product.sku.partNumber]) {
					return 0;
					//otherwise, find intersection of array of storesThatHaveAllSkus and list of stores for the new product
				} else {
					storesThatHaveAllSkus = storesThatHaveAllSkus.filter(
						(store1) =>
							nearbyStores[product.sku.partNumber].filter(
								(store2) => store1.storeRes.location === store2.storeRes.location
							).length > 0
					);
				}
			});
		}

		return storesThatHaveAllSkus?.length;
	}
);

export const selectSelectedBundlePruductsPrices = createSelector(
	selectSelectedBundleSkus,
	(selectedProducts) =>
		selectedProducts.reduce(
			(accumulator, currentValue) =>
				accumulator +
				currentValue.sku.prices.offerPrice * parseInt(currentValue.quantity, 10),
			0
		)
);

export const selectBundleInStorePickupButtonText = createSelector(
	selectSelectedBundleSkus,
	selectStoreNameTitleCase,
	findLowestBopisInventories,
	selectBundleNearbyAvailableStoreCount,
	selectUserStoreInfo,
	selectPickupTimeText,
	(
		selectedBundleSkus,
		storeName,
		inventory,
		nearbyStores,
		userStoreInfo: HeaderStoreSelection,
		pickupTimeText: string
	): FulfillmentButtonInterface => {
		const isStoreNameEmpty = !storeName;
		const isBundleSkusEmpty = selectedBundleSkus.length === 0;
		const isInventoryAvailableForPickup = inventory?.allProductsAvailableForStorePickup;
		const isInventoryAvailableInStore = inventory?.allProductsAvailableToBuyInStore;
		const isUserStoreClosed =
			userStoreInfo?.status !==
			StoreStatus.OPEN_FOR_SHIP_FROM_STORE_AND_BOPIS_AND_FOOT_TRAFFIC;

		// No user store set. This will work for sku resolved or sku not resolved
		if (isStoreNameEmpty) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				changeStoreButtonText: CHANGE_STORE_BUTTON_TEXTS.noStoreSelected,
				fulfillmentButtonDisabledClass: false,
				addToCartButtonText: isBundleSkusEmpty
					? ATC_BUTTON_TEXTS.atc
					: ATC_BUTTON_TEXTS.noStoreSelected,
				buttonAction: isBundleSkusEmpty ? BUTTON_ACTIONS.selectOptions : null,
				addToCartButtonDisabled: false,
				fulfillmentLabelTextLine1: ''
			};
		}
		if (isBundleSkusEmpty) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				fulfillmentButtonText: BUTTON_TEXTS.addToSet,
				fulfillmentButtonDisabledClass: false,
				fulfillmentLabelTextLine1: '',
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				buttonAction: BUTTON_ACTIONS.selectOptions,
				addToCartButtonDisabled: false
			};
		}
		// Store closed without nearby stores
		if (isUserStoreClosed && nearbyStores === 0) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				fulfillmentButtonText: BUTTON_TEXTS.temporarilyClosed(storeName),
				fulfillmentButtonDisabledClass: true,
				changeStoreButtonText: null,
				fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableAt,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: true
			};
		}
		// store closed with nearby stores
		if (isUserStoreClosed && nearbyStores > 0) {
			return {
				addToCartButtonDisabled: true,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				fulfillmentButtonDisabledClass: false,
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableAt,
				fulfillmentButtonText: BUTTON_TEXTS.notAvailable,
				changeStoreButtonText: CHANGE_STORE_BUTTON_TEXTS.nearbyStores,
				storeClosedMessage: STORE_CLOSED_MESSAGE.temporarilyClosed,
				notAvailable: true
			};
		}
		// Store open, avalable for pickup
		if (isInventoryAvailableForPickup) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				nonDynamicFullfillmentButtonText: BUTTON_TEXTS.inStockAtStore(storeName),
				fulfillmentButtonText: pickupTimeText,
				fulfillmentButtonDisabledClass: false,
				fulfillmentLabelTextLine1: LABEL_TEXTS.pickupAtStore,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: false
			};
		}
		// Store open, available in store
		if (isInventoryAvailableInStore) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				fulfillmentButtonText: BUTTON_TEXTS.purchaseInStore,
				fulfillmentButtonDisabledClass: true,
				fulfillmentLabelTextLine1: LABEL_TEXTS.inStockAtStore,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: true,
				availableInStoreOnly: true
			};
		}
		// Store open, not available, nearby stores
		if (nearbyStores > 0) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				fulfillmentButtonDisabledClass: false,
				changeStoreButtonText: CHANGE_STORE_BUTTON_TEXTS.nearbyStores,
				fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableAt,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: true,
				notAvailable: true
			};
		}
		// Store open, not available, no nearby stores
		return {
			fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
			fulfillmentButtonText: BUTTON_TEXTS.notAvailable,
			fulfillmentButtonDisabledClass: true,
			fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableAt,
			addToCartButtonText: ATC_BUTTON_TEXTS.atc,
			addToCartButtonDisabled: true,
			notAvailable: true
		};
	}
);

export const selectBundleShipToMeButtonText = createSelector(
	selectSelectedBundleSkus,
	areAllSelectedSkusAvailableToShip,
	(selectedBundleSkus, allSelectedSkusAvailableToShip): FulfillmentButtonInterface => {
		if (selectedBundleSkus.length === 0) {
			return {
				fulfillmentButtonText: BUTTON_TEXTS.addToSet,
				showZipCodeButton: true,
				fulfillmentButtonDisabledClass: false,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				buttonAction: BUTTON_ACTIONS.selectOptions,
				addToCartButtonDisabled: false
			};
		}
		if (allSelectedSkusAvailableToShip) {
			return {
				fulfillmentButtonText: BUTTON_TEXTS.available,
				showZipCodeButton: true,
				fulfillmentButtonDisabledClass: false,
				showEDD: true,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: false
			};
		}
		// Product is not available for Ship To Me
		return {
			fulfillmentButtonText: BUTTON_TEXTS.notAvailable,
			fulfillmentButtonDisabledClass: true,
			showZipCodeButton: true,
			fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableToShip,
			addToCartButtonText: ATC_BUTTON_TEXTS.atc,
			addToCartButtonDisabled: true,
			notAvailable: true
		};
	}
);
